import React, { Component } from "react";
import { ToastsStore } from "react-toasts";
import {
  AiProcessPromptConfig,
  AiVerificationError,
  DEFAULT_CLOSE_REMARK_OPTIONS,
  exportAiVerificationErrors,
  getAiVerificationErrors,
  getAiVerificationPrompts,
  getCloseRemarkOptionsForProcess,
  formatPageLineDisplay,
  severityBadgeClass,
  updateAiVerificationError,
} from "../../utils/aiDocumentVerification";
import "./document-verification.css";

interface Props {
  fileId: string;
  processType: string;
  /** Per-process prompts from System Verification Management (preferred for remark options). */
  processPrompts?: AiProcessPromptConfig[];
  onPendingChange?: (pendingCount: number, canComplete?: boolean) => void;
  reloadToken?: number;
  latestRun?: any;
  /** Concatenated error_title + message from Run Verification API. */
  apiErrorMessage?: string;
}

interface State {
  errors: AiVerificationError[];
  loading: boolean;
  search: string;
  statusFilter: string;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  pendingCount: number;
  openCount: number;
  closedCount: number;
  sortBy: string;
  sortDir: "asc" | "desc";
  closingId: number | null;
  closeModalError: AiVerificationError | null;
  closeModalRemark: string;
  exportLoading: boolean;
  /** Loaded when parent has not yet fetched prompts. */
  localProcessPrompts: AiProcessPromptConfig[];
}

export default class DocumentVerificationErrorTable extends Component<Props, State> {
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  state: State = {
    errors: [],
    loading: true,
    search: "",
    statusFilter: "open",
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
    pendingCount: 0,
    openCount: 0,
    closedCount: 0,
    sortBy: "created_at",
    sortDir: "desc",
    closingId: null,
    closeModalError: null,
    closeModalRemark: "",
    exportLoading: false,
    localProcessPrompts: [],
  };

  componentDidMount() {
    this.loadErrors();
    this.ensureProcessPromptsLoaded();
  }

  componentWillUnmount() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.fileId !== this.props.fileId ||
      prevProps.processType !== this.props.processType ||
      prevProps.reloadToken !== this.props.reloadToken
    ) {
      this.setState({ page: 1 }, () => this.loadErrors());
    }
    if (prevProps.fileId !== this.props.fileId) {
      this.ensureProcessPromptsLoaded();
    }
    if (
      prevProps.processPrompts !== this.props.processPrompts &&
      !this.props.processPrompts?.length
    ) {
      this.ensureProcessPromptsLoaded();
    }
    if (
      prevProps.processType !== this.props.processType ||
      prevProps.processPrompts !== this.props.processPrompts
    ) {
      const { closeModalError, closeModalRemark } = this.state;
      if (closeModalError && closeModalRemark) {
        const allowed = this.getCloseRemarkOptions(closeModalError.process_type);
        if (!allowed.includes(closeModalRemark)) {
          this.setState({ closeModalRemark: "" });
        }
      }
    }
  }

  ensureProcessPromptsLoaded = () => {
    if (this.props.processPrompts?.length) {
      return;
    }
    getAiVerificationPrompts()
      .then((response) => {
        if (response.success && Array.isArray(response.data)) {
          this.setState({ localProcessPrompts: response.data as AiProcessPromptConfig[] });
        }
      })
      .catch(() => {});
  };

  loadErrors = () => {
    this.setState({ loading: true });
    const { search, statusFilter, page, pageSize, sortBy, sortDir } = this.state;

    getAiVerificationErrors({
      file_idfk: this.props.fileId,
      process_type: this.props.processType,
      search,
      status: statusFilter,
      page,
      page_size: pageSize,
      sort_by: sortBy,
      sort_dir: sortDir,
    })
      .then((response) => {
        if (response?.success) {
          const apiErrors = Array.isArray(response.data) ? response.data : [];
          const pending = Number(response.pending_count) || 0;
          const statusCounts = response.status_counts || {};
          this.setState({
            errors: apiErrors,
            total: response.pagination?.total ?? apiErrors.length,
            totalPages: response.pagination?.total_pages ?? 1,
            pendingCount: pending,
            openCount: Number(statusCounts.open) || 0,
            closedCount: Number(statusCounts.closed) || 0,
            loading: false,
          });
          if (this.props.onPendingChange) {
            this.props.onPendingChange(pending, pending === 0);
          }
          return;
        }
        this.setState({
          errors: [],
          total: 0,
          totalPages: 0,
          pendingCount: 0,
          openCount: 0,
          closedCount: 0,
          loading: false,
        });
        if (this.props.onPendingChange) {
          this.props.onPendingChange(0, true);
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        ToastsStore.error("Failed to load verification errors");
      });
  };

  handleSearchInputChange = (value: string) => {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.setState({ search: value, page: 1 });
    this.searchDebounceTimer = setTimeout(() => {
      this.loadErrors();
    }, 350);
  };

  handleSort = (column: string) => {
    this.setState(
      (prev) => ({
        sortBy: column,
        sortDir: prev.sortBy === column && prev.sortDir === "asc" ? "desc" : "asc",
        page: 1,
      }),
      () => this.loadErrors(),
    );
  };

  openCloseModal = (error: AiVerificationError) => {
    this.setState({
      closeModalError: error,
      closeModalRemark: "",
    });
  };

  closeCloseModal = () => {
    if (this.state.closingId) return;
    this.setState({ closeModalError: null, closeModalRemark: "" });
  };

  handleCloseModalRemarkChange = (value: string) => {
    this.setState({ closeModalRemark: value });
  };

  getCloseErrorToastMessage = (response: any): string => {
    const raw = response && response.message;
    if (typeof raw === "string" && raw.trim()) {
      return raw;
    }
    if (raw && typeof raw === "object") {
      if (typeof raw.error === "string" && raw.error.trim()) {
        return raw.error;
      }
      if (typeof raw.message === "string" && raw.message.trim()) {
        return raw.message;
      }
      try {
        const encoded = JSON.stringify(raw);
        if (encoded && encoded !== "{}") {
          return encoded;
        }
      } catch (_err) {
        /* fall through */
      }
    }
    return "Failed to close error";
  };

  confirmCloseError = () => {
    const { closeModalError, closeModalRemark } = this.state;
    if (!closeModalError) return;

    const remarks = closeModalRemark.trim();
    if (!remarks) {
      ToastsStore.error("Select a remark to close this error");
      return;
    }

    const allowedRemarks = this.getCloseRemarkOptions(closeModalError.process_type);
    if (!allowedRemarks.includes(remarks)) {
      ToastsStore.error(`Select a valid remark: ${allowedRemarks.join(", ")}`);
      return;
    }

    const user: any = localStorage.getItem("user");
    const userId = user ? JSON.parse(user)["userId"] : "";

    this.setState({ closingId: closeModalError.id });
    updateAiVerificationError({
      error_id: closeModalError.id,
      status: "closed",
      remarks,
      userId,
    })
      .then((response) => {
        this.setState({ closingId: null });
        if (response.success) {
          ToastsStore.success("Error closed successfully");
          const filePending = response.file_pending_count ?? response.pending_count ?? 0;
          if (this.props.onPendingChange) {
            this.props.onPendingChange(filePending);
          }
          this.setState({ closeModalError: null, closeModalRemark: "" });
          this.loadErrors();
        } else {
          ToastsStore.error(this.getCloseErrorToastMessage(response));
        }
      })
      .catch(() => {
        this.setState({ closingId: null });
        ToastsStore.error("Failed to close error");
      });
  };

  reopenError = (error: AiVerificationError) => {
    const user: any = localStorage.getItem("user");
    const userId = user ? JSON.parse(user)["userId"] : "";
    updateAiVerificationError({
      error_id: error.id,
      status: "open",
      remarks: error.remarks || "",
      userId,
    }).then((response) => {
      if (response.success) {
        ToastsStore.success("Error reopened");
        if (this.props.onPendingChange) {
          this.props.onPendingChange(response.file_pending_count ?? 0);
        }
        this.loadErrors();
      }
    });
  };

  getProcessPrompts(): AiProcessPromptConfig[] {
    return this.props.processPrompts?.length
      ? this.props.processPrompts
      : this.state.localProcessPrompts;
  }

  getCloseRemarkOptions(forProcessType?: string): string[] {
    const processType = forProcessType || this.props.processType;
    const prompts = this.getProcessPrompts();
    if (prompts.length) {
      return getCloseRemarkOptionsForProcess(prompts, processType);
    }
    return [...DEFAULT_CLOSE_REMARK_OPTIONS];
  }

  renderCloseErrorModal() {
    const { closeModalError, closeModalRemark, closingId } = this.state;
    const closeRemarkOptions = this.getCloseRemarkOptions(closeModalError?.process_type);
    if (!closeModalError) return null;

    return (
      <div className="ai-close-error-modal">
        <div
          className="ai-close-error-modal-backdrop"
          onClick={this.closeCloseModal}
          role="presentation"
        />
        <div className="ai-close-error-modal-dialog" role="dialog" aria-modal="true">
          <div className="ai-close-error-modal-content">
            <div className="ai-close-error-modal-header">
              <h5 className="mb-0">Close Error</h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                disabled={!!closingId}
                onClick={this.closeCloseModal}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="ai-close-error-modal-body">
              <p className="text-muted small mb-3">
                Select a remark and save to close this error. Each error must be closed one at a time.
              </p>
              <div className="ai-close-error-preview">
                <div>
                  <strong>Section / Field</strong>
                  <div>{closeModalError.section_field_name || "—"}</div>
                </div>
                <div>
                  <strong>Error Description</strong>
                  <div>{closeModalError.error_description}</div>
                </div>
                <div>
                  <strong>Mention 1</strong>
                  <div>
                    {closeModalError.mention1_value || closeModalError.mention1_section ? (
                      <>
                        <span className="ai-mention-value">{closeModalError.mention1_value}</span>
                        {closeModalError.mention1_section
                          ? ` ${closeModalError.mention1_section}`
                          : ""}
                      </>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
                <div>
                  <strong>Mention 2</strong>
                  <div>
                    {closeModalError.mention2_value || closeModalError.mention2_section ? (
                      <>
                        <span className="ai-mention-value">{closeModalError.mention2_value}</span>
                        {closeModalError.mention2_section
                          ? ` ${closeModalError.mention2_section}`
                          : ""}
                      </>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              </div>
              <div className="form-group mb-0">
                <label className="font-weight-bold">Remarks</label>
                <select
                  className="form-control"
                  value={closeModalRemark}
                  onChange={(e) => this.handleCloseModalRemarkChange(e.target.value)}
                  disabled={!!closingId}
                >
                  <option value="">Select remark...</option>
                  {closeRemarkOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="ai-close-error-modal-footer">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={!!closingId}
                onClick={this.closeCloseModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success btn-sm"
                disabled={!!closingId || !closeModalRemark}
                onClick={this.confirmCloseError}
              >
                {closingId ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-1" />
                    Saving...
                  </>
                ) : (
                  "Save & Close Error"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  exportCsv = () => {
    this.setState({ exportLoading: true });
    exportAiVerificationErrors({
      file_idfk: this.props.fileId,
      process_type: this.props.processType,
      status: this.state.statusFilter,
      format: "csv",
    })
      .then(async (res) => {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `system_verification_${this.props.processType}_${this.props.fileId}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        this.setState({ exportLoading: false });
        ToastsStore.success("Report downloaded");
      })
      .catch(() => {
        this.setState({ exportLoading: false });
        ToastsStore.error("Export failed");
      });
  };

  render() {
    const {
      errors,
      loading,
      search,
      statusFilter,
      page,
      pageSize,
      totalPages,
      total,
      closingId,
      exportLoading,
      pendingCount,
      openCount,
      closedCount,
    } = this.state;
    const latestRun = this.props.latestRun;
    const lastRunStatus = String(latestRun?.run_status || "").toLowerCase();
    const isLastRunFailed = lastRunStatus === "failed";
    const isLastRunRunning = lastRunStatus === "running";
    const isLastRunActionCompleted =
      String(latestRun?.action || "").toLowerCase() === "completed";
    const isResubmitRequiredAllClosed =
      String(latestRun?.action || "").toLowerCase() === "resubmit_required" &&
      openCount === 0;
    const processingError = String(latestRun?.processing_error || "").trim();
    const apiErrorMessage = String(this.props.apiErrorMessage || "").trim();

    return (
      <>
      <div className="ai-verification-table-wrap">
        <div className="ai-verification-error-summary">
          <div className="ai-error-summary-left">
            <span className="ai-error-summary-item ai-error-summary-item--open">
              <strong>{openCount}</strong> Open
            </span>
            <span className="ai-error-summary-item ai-error-summary-item--closed">
              <strong>{closedCount}</strong> Closed
            </span>
            <span className="ai-error-summary-item ai-error-summary-item--rows">
              {total} Row{total !== 1 ? "s" : ""}
            </span>
          </div>
          {apiErrorMessage ? (
            <div className="ai-error-summary-failed" title={apiErrorMessage}>
              <span className="ai-error-summary-failed-db">{apiErrorMessage}</span>
            </div>
          ) : isLastRunFailed ? (
            <div className="ai-error-summary-failed" title={processingError || undefined}>
              {processingError ? (
                <span className="ai-error-summary-failed-db">{processingError}</span>
              ) : null}
              {processingError ? " " : null}
              <span className="ai-error-summary-failed-hint">
                Please execute Run Verification again
              </span>
            </div>
          ) : isLastRunRunning ? (
            <div className="ai-error-summary-progress">
             System Verification in Progress, Please wait.....
            </div>
          ) : isResubmitRequiredAllClosed ? (
            <div className="ai-error-summary-success">
              All issues have been resolved. Proceed with verification again.
            </div>
          ) : isLastRunActionCompleted ? (
            <div className="ai-error-summary-success">
              No issues found, Proceed to complete the file.
            </div>
          ) : (
            <div className="ai-error-summary-center" />
          )}
          <span className="ai-error-summary-pending">Pending: {pendingCount}</span>
        </div>

        <div className="ai-verification-toolbar">
          <input
            type="text"
            className="form-control form-control-sm ai-toolbar-input"
            placeholder="Search errors..."
            value={search}
            onChange={(e) => this.handleSearchInputChange(e.target.value)}
          />
          <select
            className="form-control form-control-sm ai-toolbar-select"
            value={statusFilter}
            onChange={(e) =>
              this.setState({ statusFilter: e.target.value, page: 1 }, () => this.loadErrors())
            }
          >
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
          <button
            type="button"
            className="btn btn-sm btn-outline-success"
            disabled={exportLoading}
            onClick={this.exportCsv}
          >
            Download Report (CSV)
          </button>
        </div>

        {loading ? (
          <div className="text-center py-4 ai-verification-loading">
            <i className="fa fa-spinner fa-spin" /> Loading errors...
          </div>
        ) : (
          <div className="ai-verification-table-scroll">
            <table className="table table-bordered table-sm ai-verification-table mb-0">
              <colgroup>
                <col className="ai-col-index" />
                <col className="ai-col-section" />
                <col className="ai-col-error" />
                <col className="ai-col-correction" />
                <col className="ai-col-correction" />
                <col className="ai-col-severity" />
                <col className="ai-col-status-action" />
              </colgroup>
              <thead>
                <tr>
                  <th className="ai-col-index">#</th>                  
                  <th
                    onClick={() => this.handleSort("section_field_name")}
                    className="ai-th-label-normal ai-sortable ai-col-section"
                  >
                    Section / Field
                  </th>
                  <th className="ai-th-label-normal ai-col-error">Error Description</th>
                  <th className="ai-th-label-normal ai-col-correction">Mention 1</th>
                  <th className="ai-th-label-normal ai-col-correction">Mention 2</th>
                  <th
                    onClick={() => this.handleSort("severity")}
                    className="ai-th-label-normal ai-sortable ai-col-severity"
                  >
                    Severity (Mistake Level)
                  </th>
                  <th
                    onClick={() => this.handleSort("status")}
                    className="ai-th-label-normal ai-sortable ai-col-status-action"
                  >
                    Status/Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {errors.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      {search || statusFilter
                        ? "No errors match your filter."
                        : "No verification errors yet. Click Run Verification to analyze the document."}
                    </td>
                  </tr>
                ) : (
                  errors.map((err, index) => (
                    <tr
                      key={err.id}
                      className={err.status === "open" ? "ai-error-row-open" : "ai-error-row-closed"}
                    >
                      <td className="text-muted ai-col-index">
                        {(page - 1) * pageSize + index + 1}
                      </td>
                      
                      <td className="ai-col-section">
                        <strong>{err.section_field_name || "—"}</strong>
                      </td>
                      <td className="ai-col-error">{err.error_description}</td>
                      <td className="ai-col-correction"><span className="ai-mention-value">{err.mention1_value}</span>&nbsp;{err.mention1_section}</td>
                      <td className="ai-col-correction"><span className="ai-mention-value">{err.mention2_value}</span>&nbsp;{err.mention2_section}</td>
                      <td className="ai-col-severity">
                        <span className={`badge ${severityBadgeClass(err.severity)}`}>
                          {err.severity}
                        </span>
                      </td>
                      <td className="ai-col-status-action">
                        <div className="ai-status-action-cell">
                          <strong
                            className={
                              err.status === "open"
                                ? "ai-status-text-open"
                                : "ai-status-text-closed"
                            }
                          >
                            {err.status}
                          </strong>
                          {err.status === "open" ? (
                            <>
                              <span className="ai-status-action-sep"> / </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                disabled={!!closingId}
                                onClick={() => this.openCloseModal(err)}
                              >
                                Close
                              </button>
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <small className="text-muted">
              Page {page} of {totalPages} ({total} records)
            </small>
            <div>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary mr-1"
                disabled={page <= 1}
                onClick={() => this.setState({ page: page - 1 }, () => this.loadErrors())}
              >
                Prev
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                disabled={page >= totalPages}
                onClick={() => this.setState({ page: page + 1 }, () => this.loadErrors())}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      {this.renderCloseErrorModal()}
      </>
    );
  }
}
